// Real-engine Organization settings. The maket Organization renders a rich
// Shopify-pattern card layout (business details + contact + address + defaults)
// whose fields the backend does not produce. This lean component wires the REAL
// GET /api/v1/orgs/{org_id} endpoint — org_id resolved from /me active_membership
// (NOT hardcoded) — and renders only the org profile the backend actually owns
// (OrgRead: name, slug, org_type, status, scope_family, timestamps). Honest
// empty state when the logged-in user has no active membership.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, Check, Copy } from 'lucide-react'
import { API_BASE } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { formatDate } from '../../lib/datetime'
import { Badge } from '../../components/ui/badge'
import { useT } from '../../i18n'
import { realOrganizationI18n } from '../../i18n/settings/real-organization'

type OrgRead = {
  id: string
  scope_family_id: string
  org_type: string
  name: string
  slug: string | null
  status: string
  created_at: string
  updated_at: string
}

const STATUS_VARIANT: Record<
  string,
  'default' | 'success' | 'warning' | 'destructive' | 'neutral'
> = {
  active: 'success',
  suspended: 'warning',
  deleted: 'destructive',
}

// The organization id, shown and copyable. It is needed for work that happens
// outside this screen — the inventory rollout cohort is the current one — and
// a platform-owned A1 organization never appears in the buy-side org picker,
// so its own profile is the only place it can be read.
function OrgIdField({ orgId }: { orgId: string }) {
  const t = useT(realOrganizationI18n)
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  async function copy() {
    try {
      await navigator.clipboard.writeText(orgId)
      setState('copied')
    } catch {
      // Refused outside a secure context and in some embedded browsers. Say so
      // rather than looking successful — the id stays selectable either way.
      setState('failed')
    }
    window.setTimeout(() => setState('idle'), 2000)
  }

  return (
    <div className="flex flex-col gap-0.5 sm:col-span-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
        {t.orgIdLabel}
      </span>
      <span className="flex items-center gap-2">
        <code className="select-all font-mono text-sm text-slate-900 appearance-dark:text-[var(--color-text)]">{orgId}</code>
        <button
          type="button"
          onClick={copy}
          aria-label={t.copyOrgId}
          title={
            state === 'failed'
              ? t.copyFailed
              : t.copyOrgId
          }
          className="rounded p-1 text-slate-400 appearance-dark:text-[var(--color-text-muted)] hover:bg-slate-100 appearance-dark:hover:bg-[var(--color-surface-muted)] hover:text-slate-700 appearance-dark:hover:text-[var(--color-text)]"
        >
          {state === 'copied' ? (
            <Check size={14} className="text-emerald-600 appearance-dark:text-[var(--color-success)]" aria-hidden="true" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
        </button>
        <span className="sr-only" role="status">
          {state === 'copied'
            ? t.copied
            : state === 'failed'
              ? t.copyFailed
              : ''}
        </span>
      </span>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
        {label}
      </span>
      <span className="text-sm text-slate-900 appearance-dark:text-[var(--color-text)]">{value}</span>
    </div>
  )
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown }
    const detail = body.detail
    const message =
      typeof detail === 'string'
        ? detail
        : detail && typeof detail === 'object' && 'message' in detail
          ? String((detail as { message: unknown }).message)
          : `HTTP ${res.status}`
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

export function RealOrganization() {
  const t = useT(realOrganizationI18n)
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const orgId = me.data?.active_membership?.org_id ?? null

  const org = useQuery({
    queryKey: ['orgs', orgId],
    queryFn: () => getJson<OrgRead>(`/orgs/${orgId}`),
    enabled: Boolean(orgId),
  })
  const statusLabel = (status: string) =>
    status in t.statusLabels
      ? t.statusLabels[status as keyof typeof t.statusLabels]
      : status

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
          {t.pageDescription}
        </p>
      </header>

      {me.isLoading || (orgId && org.isLoading) ? (
        <p className="text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{t.loading}</p>
      ) : !orgId ? (
        <div className="rounded-lg border border-dashed border-slate-300 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-10 text-center">
          <Building2 className="mx-auto h-8 w-8 text-slate-300 appearance-dark:text-[var(--color-text-muted)]" />
          <p className="mt-3 text-sm font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">
            {t.noOrgTitle}
          </p>
          <p className="mt-1 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
            {t.noOrgBody}
          </p>
        </div>
      ) : org.isError || !org.data ? (
        <p className="text-sm text-rose-600 appearance-dark:text-[var(--color-danger)]">
          {t.loadError(String(org.error))}
        </p>
      ) : (
        <section className="rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)]">
          <header className="flex items-start justify-between gap-3 border-b border-slate-100 appearance-dark:border-[var(--color-border)] px-5 py-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">
                {org.data.name}
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
                {t.platformRecord}
              </p>
            </div>
            <Badge variant={STATUS_VARIANT[org.data.status] ?? 'neutral'}>
              {statusLabel(org.data.status)}
            </Badge>
          </header>
          <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
            <OrgIdField orgId={org.data.id} />
            <Field label={t.fields.name} value={org.data.name} />
            <Field label={t.fields.slug} value={org.data.slug ?? '—'} />
            <Field label={t.fields.orgType} value={org.data.org_type} />
            <Field label={t.fields.status} value={statusLabel(org.data.status)} />
            <Field
              label={t.fields.created}
              value={formatDate(org.data.created_at, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            />
            <Field
              label={t.fields.updated}
              value={formatDate(org.data.updated_at, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            />
          </div>
        </section>
      )}

      <p className="text-xs text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
        {t.footer}
      </p>
    </div>
  )
}
